import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Award, Users, Globe, Zap, Cloud, MonitorCog, ShieldCheck, Settings, Briefcase, ArrowRight, CheckCircle2 } from 'lucide-react';
import { aboutPage, companyInfo, globalDatacenters, mspCapabilities, images } from '../mock/mock';
import Navbar from './Navbar';
import Footer from './Footer';
import WhyChoose from './WhyChoose';
import StatsBanner from './StatsBanner';
import FAQ from './FAQ';

const iconMap = { Cloud, MonitorCog, ShieldCheck, Settings, Briefcase };

const AboutPage = () => {
  return (
    <>
      <Navbar />

      {/* Breadcrumb hero */}
      <section className="relative pt-16 pb-24 section-mesh overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-25" />
        <div className="absolute inset-0">
          <img src={images.heroBg} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070f]/70 via-[#05070f]/85 to-[#05070f]" />
        </div>
        <div className="container-x px-6 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-6">
              <Link to="/" className="hover:text-cyan-300">Home</Link>
              <ChevronRight size={14} />
              <span className="text-cyan-300">About Us</span>
            </div>
            <div className="eyebrow mb-4 mx-auto"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> About DC10G</div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] tracking-tight">
              Powering the <span className="gradient-text">digital era</span><br />since <span className="gradient-text-warm">2006</span>.
            </h1>
            <p className="section-subtitle mx-auto mt-6">A prominent Managed Service Partner enabling businesses with world-class datacenter, cloud and cybersecurity solutions.</p>
          </motion.div>
        </div>
      </section>

      {/* Welcome */}
      <section className="section-padding section-dark">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img src={images.about} alt="DC10G Datacenter" className="w-full h-[520px] object-cover" />
            </div>
            <motion.div initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, type: 'spring' }}
              className="absolute -bottom-8 -right-6 glass-strong rounded-2xl p-5 border border-cyan-400/30 shadow-2xl shadow-cyan-500/20">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                  <Award className="text-white" size={22} />
                </div>
                <div>
                  <div className="text-xs text-slate-400">Managed Service Partner</div>
                  <div className="font-bold text-white">Top MSP in India</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="eyebrow mb-4"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Our Story</div>
            <h2 className="section-title">{aboutPage.heading}</h2>
            <div className="mt-6 space-y-4 text-slate-300 leading-relaxed">
              {aboutPage.paragraphs.map((p, i) => (<p key={i}>{p}</p>))}
            </div>
            <Link to="/contact-us" className="btn-primary mt-8">Contact Us <ArrowRight size={16} /></Link>
          </motion.div>
        </div>
      </section>

      {/* Core Capabilities as MSP */}
      <section className="section-padding section-mesh">
        <div className="container-x relative">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow mb-4"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Core Capabilities</div>
            <h2 className="section-title">A complete <span className="gradient-text">MSP portfolio</span></h2>
            <p className="section-subtitle mx-auto mt-4">From cloud to cybersecurity — everything a modern enterprise needs, delivered as a managed service.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {mspCapabilities.map((c, i) => {
              const Icon = iconMap[c.icon] || Cloud;
              return (
                <motion.div key={c.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  whileHover={{ y: -6 }}
                  className="group p-6 rounded-2xl glass hover:border-cyan-400/40 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform">
                    <Icon className="text-white" size={22} />
                  </div>
                  <h3 className="font-bold text-white text-base">{c.title}</h3>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{c.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Data Centers */}
      <section className="section-padding section-dark">
        <div className="container-x relative">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow mb-4"><Globe size={12} /> Global Presence</div>
            <h2 className="section-title">Data Centers around <span className="gradient-text">the world</span></h2>
            <p className="section-subtitle mx-auto mt-4">Partnered with leading data centers globally to deliver low-latency service wherever your customers are.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {globalDatacenters.map((c, i) => (
              <motion.div key={c.country} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                whileHover={{ y: -5 }}
                className="p-5 rounded-2xl glass hover:border-cyan-400/40 text-center transition-all">
                <img src={c.flag} alt={c.country} className="w-14 h-10 object-cover rounded mx-auto mb-3 shadow-md" />
                <div className="font-bold text-white text-sm">{c.country}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{c.code}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WhyChoose />
      <StatsBanner />

      {/* CTA */}
      <section className="section-padding section-mesh">
        <div className="container-x">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl glass-strong border border-cyan-400/25 p-10 md:p-16 text-center">
            <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl" />
            <div className="relative">
              <h2 className="section-title">Ready to transform your <span className="gradient-text">infrastructure?</span></h2>
              <p className="section-subtitle mx-auto mt-4">Get a personalized consultation and quote within 24 hours.</p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/contact-us" className="btn-primary-neon">Talk to Sales <ArrowRight size={16} /></Link>
                <a href="/#services" className="btn-ghost-neon">Explore Services</a>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2">
                {['24-hour response', 'Free consultation', 'Custom enterprise quote'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5 text-sm text-slate-300">
                    <CheckCircle2 size={14} className="text-cyan-400" /> {t}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <FAQ />
      <Footer />
    </>
  );
};

export default AboutPage;
